import { useNavigate } from "react-router-dom";
import { ChevronRight, Scissors, Home, Search } from "lucide-react";
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

const ErrorPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('misc');

  return (
    <div className="min-h-screen font-sans bg-white flex flex-col">
      <Helmet>
        <title>{t('error_meta_title') || 'Страницата не е намерена | Salonite365'}</title>
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      {/* Background blobs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full opacity-20"
          style={{ background: "radial-gradient(circle, rgba(130,178,192,0.6) 0%, transparent 70%)" }}
        />
        <div
          className="absolute top-1/3 -right-40 w-[600px] h-[600px] rounded-full opacity-15"
          style={{ background: "radial-gradient(circle, rgba(163,70,213,0.5) 0%, transparent 70%)" }}
        />
        <div
          className="absolute -bottom-20 left-1/3 w-[400px] h-[400px] rounded-full opacity-20"
          style={{ background: "radial-gradient(circle, rgba(240,226,195,0.8) 0%, transparent 70%)" }}
        />
      </div>

      <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 py-20 text-center font-dm">

        {/* Logo mark */}
        <div className="mb-10">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto"
            style={{
              background: "linear-gradient(135deg, rgba(130,178,192,0.3) 0%, rgba(163,70,213,0.2) 100%)",
              border: "1px solid rgba(163,70,213,0.15)",
            }}
          >
            <Scissors className="w-7 h-7" style={{ color: "#a346d5" }} />
          </div>
        </div>

        {/* 404 number */}
        <div className="relative mb-6 select-none">
          <p
            className="font-playfair font-black leading-none"
            style={{
              fontSize: "clamp(8rem, 22vw, 18rem)",
              background: "linear-gradient(135deg, rgba(195,222,221,0.6) 0%, rgba(209,183,243,0.4) 50%, rgba(240,226,195,0.5) 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              letterSpacing: "-0.05em",
              lineHeight: 0.9,
            }}
          >
            404
          </p>
          <p
            className="font-playfair font-black leading-none absolute inset-0 opacity-10"
            style={{
              fontSize: "clamp(8rem, 22vw, 18rem)",
              WebkitTextStroke: "2px #a346d5",
              WebkitTextFillColor: "transparent",
              letterSpacing: "-0.05em",
              lineHeight: 0.9,
            }}
          >
            404
          </p>
        </div>

        {/* Heading */}
        <h1
          className="font-playfair font-black text-stone-900 mb-4"
          style={{ fontSize: "clamp(1.6rem, 4vw, 2.8rem)", letterSpacing: "-0.03em" }}
        >
          {t('error_title')}
        </h1>

        {/* Subtext */}
        <p className="text-stone-500 max-w-md mx-auto leading-relaxed mb-10" style={{ fontSize: "1rem" }}>
          {t('error_subtitle')}
        </p>

        {/* Divider */}
        <div className="flex items-center gap-4 mb-10 w-full max-w-xs">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent to-[#F0E2C3]" />
          <div
            className="w-2 h-2 rounded-full"
            style={{ background: "linear-gradient(135deg, #82B2C0, #a346d5)" }}
          />
          <div className="flex-1 h-px bg-gradient-to-l from-transparent to-[#F0E2C3]" />
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row items-center gap-3">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 px-7 py-3.5 rounded-2xl text-white font-bold text-sm transition-all duration-200 hover:scale-[1.03]"
            style={{
              background: "linear-gradient(180deg, #d1b7f3 0%, #a346d5 100%)",
              boxShadow: "0 8px 24px rgba(163,70,213,0.35)",
            }}
          >
            <Home className="w-4 h-4" />
            {t('error_home')}
            <ChevronRight className="w-4 h-4" />
          </button>

          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-7 py-3.5 rounded-2xl text-stone-600 font-bold text-sm transition-all duration-200 hover:bg-[#faf5ff] hover:text-purple-700"
            style={{ background: "#F2EAE0", border: "1px solid #F0E2C3" }}
          >
            {t('error_back')}
          </button>
        </div>
      </main>
    </div>
  );
};

export default ErrorPage;